import React, { useState, useEffect } from "react";
import { utils, writeFile } from "xlsx";
import { getRecentUnsubscribes } from "../../../services/partner.service.ts";
import { getProfileImage } from "../../../services/profileImageCache.ts";
import dayjs, { Dayjs } from "dayjs";
import { Tooltip, Menu, MenuItem } from "@mui/material";
import { DatePicker } from "antd";
import locale from "antd/es/date-picker/locale/es_ES";

const { RangePicker } = DatePicker;

interface UltimasBajasProps {
  startDate?: string;
  endDate?: string;
  showChart?: boolean;
  onDateRangeChange?: (startDate?: string, endDate?: string) => void;
}

interface UnsubscribeItem {
  id: number;
  partnerId: number;
  name: string;
  position: string;
  branch: string;
  banDate: string;
  reasonUnsubscribe: string | null;
}

interface UnsubscribeItemWithImage extends UnsubscribeItem {
  profileImage: string | null;
}

const toTitleCase = (text: string): string => {
  if (!text) return "";
  return text
    .toLowerCase()
    .split(/\s+/)
    .map((word) => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const formatName = (fullName: string): string => {
  if (!fullName) return "";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length >= 3) {
    return toTitleCase(`${parts[0]} ${parts[parts.length - 2]}`);
  }
  return toTitleCase(fullName);
};

const UltimasBajas: React.FC<UltimasBajasProps> = ({
  startDate,
  endDate,
  onDateRangeChange,
}) => {
  const [data, setData] = useState<UnsubscribeItemWithImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    partnerId: number;
  } | null>(null);
  const [dateRange, setDateRange] = React.useState<[Dayjs, Dayjs] | null>(
    () => {
      if (startDate && endDate) {
        return [dayjs(startDate), dayjs(endDate)];
      }
      const end = dayjs();
      const start = end.subtract(30, "day");
      return [start, end];
    }
  );
  const [debounceTimer, setDebounceTimer] = React.useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (startDate && endDate) {
      setDateRange([dayjs(startDate), dayjs(endDate)]);
    }
  }, [startDate, endDate]);

  const handleDateChange = (dates: (Dayjs | null)[] | null) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
      const startDateStr = dates[0].format("YYYY-MM-DD");
      const endDateStr = dates[1].format("YYYY-MM-DD");

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      const timer = setTimeout(() => {
        if (onDateRangeChange) {
          onDateRangeChange(startDateStr, endDateStr);
        }
      }, 500);

      setDebounceTimer(timer);
    }
  };

  React.useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await getRecentUnsubscribes(
          undefined,
          startDate,
          endDate
        );

        const itemsWithImages = await Promise.all(
          result.map(async (item: UnsubscribeItem) => {
            const profileImage = await getProfileImage(
              item.partnerId || item.id
            );
            return {
              ...item,
              profileImage,
            };
          })
        );

        setData(itemsWithImages);
      } catch (error) {
        console.error("Error fetching recent unsubscribes:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  const exportToExcel = () => {
    const ws = utils.aoa_to_sheet([]);
    const headers = ["Nombre", "Puesto", "Sucursal", "Fecha de Baja", "Motivo"];
    utils.sheet_add_aoa(ws, [headers], { origin: "A1" });

    let rowIndex = 2;
    data.forEach((item) => {
      const row = [
        item.name,
        item.position,
        item.branch,
        dayjs(item.banDate).format("DD/MM/YYYY"),
        item.reasonUnsubscribe || "Sin motivo",
      ];
      utils.sheet_add_aoa(ws, [row], { origin: `A${rowIndex}` });
      rowIndex++;
    });

    ws["!cols"] = [
      { wch: 35 },
      { wch: 30 },
      { wch: 25 },
      { wch: 15 },
      { wch: 30 },
    ];

    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Últimas Bajas");
    writeFile(wb, `Ultimas_Bajas_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  if (loading) {
    return (
      <div style={{ ...styles.chartCard, width: "100%", minHeight: "360px" }}>
        <div style={styles.chartHeader}>
          <div style={styles.chartHeaderContent}>
            <h3 style={styles.chartTitle}>
              <i
                className="fa-solid fa-user-minus"
                style={{ ...styles.chartTitleIcon, color: "#ef4444" }}
              ></i>
              Últimas Bajas
            </h3>
          </div>
        </div>
        <div
          style={{
            ...styles.chartBody,
            textAlign: "center",
            padding: "40px",
            minHeight: "280px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i
            className="fa-solid fa-spinner fa-spin"
            style={{ fontSize: "24px", color: "#6b7280" }}
          ></i>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        ...styles.chartCard,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minHeight: "360px",
      }}
    >
      <div style={styles.chartHeader}>
        <div style={styles.chartHeaderContent}>
          <h3 style={styles.chartTitle}>
            <i
              className="fa-solid fa-user-minus"
              style={{ ...styles.chartTitleIcon, color: "#ef4444" }}
            ></i>
            Últimas Bajas
          </h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {dateRange && (
              <span style={styles.dateLabel}>
                {dateRange[0].format("DD/MM/YYYY")} -{" "}
                {dateRange[1].format("DD/MM/YYYY")}
              </span>
            )}
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setPickerOpen(!pickerOpen)}
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "6px 10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#6b7280",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                }}
                title="Seleccionar rango de fechas"
              >
                <i
                  className="fa-regular fa-calendar"
                  style={{ fontSize: "14px" }}
                ></i>
              </button>
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  zIndex: 1000,
                  visibility: pickerOpen ? "visible" : "hidden",
                  opacity: pickerOpen ? 1 : 0,
                }}
              >
                <RangePicker
                  open={pickerOpen}
                  onOpenChange={(open) => setPickerOpen(open)}
                  format="DD [de] MMMM [de] YYYY"
                  placeholder={["Fecha de inicio", "Fecha de fin"]}
                  size="small"
                  allowClear={false}
                  value={dateRange}
                  onChange={handleDateChange}
                  locale={locale}
                  style={{
                    width: "0px",
                    height: "0px",
                    padding: 0,
                    border: "none",
                    overflow: "hidden",
                    position: "absolute",
                  }}
                  renderExtraFooter={() => <div style={{ padding: 8 }} />}
                />
              </div>
            </div>
            <button
              onClick={exportToExcel}
              style={styles.exportChartButton}
              title="Descargar Excel"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#16a34a";
                e.currentTarget.style.borderColor = "#16a34a";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#22c55e";
                e.currentTarget.style.borderColor = "#22c55e";
              }}
            >
              <i
                className="fa-solid fa-file-arrow-down"
                style={styles.exportChartIcon}
              ></i>
              <span style={styles.exportChartText}>Excel</span>
            </button>
          </div>
        </div>
      </div>
      <div
        style={{
          ...styles.chartBody,
          display: "flex",
          flexDirection: "column",
          padding: "0",
          minHeight: "260px",
        }}
      >
        {data.length > 0 ? (
          <div
            style={{
              height: "260px",
              overflowY: "auto",
              padding: "12px",
            }}
          >
            <div style={styles.listContainer}>
              {data.map((item) => {
                const tooltipContent = (
                  <div style={{ padding: "4px" }}>
                    <div style={styles.tooltipRow}>
                      <i
                        className="fa-solid fa-user"
                        style={styles.tooltipIcon}
                      ></i>
                      <span style={styles.tooltipLabel}>Nombre:</span>
                      <span style={styles.tooltipValue}>
                        {toTitleCase(item.name)}
                      </span>
                    </div>
                    <div style={styles.tooltipRow}>
                      <i
                        className="fa-solid fa-calendar"
                        style={styles.tooltipIcon}
                      ></i>
                      <span style={styles.tooltipLabel}>Fecha:</span>
                      <span style={styles.tooltipValue}>
                        {dayjs(item.banDate).format("DD/MM/YYYY")}
                      </span>
                    </div>
                    <div style={styles.tooltipRow}>
                      <i
                        className="fa-solid fa-building"
                        style={styles.tooltipIcon}
                      ></i>
                      <span style={styles.tooltipLabel}>Sucursal:</span>
                      <span style={styles.tooltipValue}>{item.branch}</span>
                    </div>
                    {item.reasonUnsubscribe && (
                      <div style={styles.tooltipRow}>
                        <i
                          className="fa-solid fa-comment"
                          style={styles.tooltipIcon}
                        ></i>
                        <span style={styles.tooltipLabel}>Motivo:</span>
                        <span style={styles.tooltipValue}>
                          {item.reasonUnsubscribe}
                        </span>
                      </div>
                    )}
                  </div>
                );

                return (
                  <Tooltip
                    key={item.id}
                    title={tooltipContent}
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "#1f2937",
                          color: "#fff",
                          fontSize: "12px",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          maxWidth: "none",
                        },
                      },
                      arrow: {
                        sx: {
                          color: "#1f2937",
                        },
                      },
                    }}
                  >
                    <div
                      style={styles.card}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setContextMenu(
                          contextMenu === null
                            ? {
                                mouseX: e.clientX + 2,
                                mouseY: e.clientY - 6,
                                partnerId: item.partnerId || item.id,
                              }
                            : null
                        );
                      }}
                    >
                      <div style={styles.avatarContainer}>
                        {item.profileImage ? (
                          <img
                            src={item.profileImage}
                            alt={toTitleCase(item.name)}
                            style={styles.avatarImage}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const icon = e.currentTarget
                                .nextElementSibling as HTMLElement;
                              if (icon) icon.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <div
                          style={{
                            ...styles.avatarIcon,
                            display: item.profileImage ? "none" : "flex",
                          }}
                        >
                          <i
                            className="fa-regular fa-user"
                            style={styles.avatarIconInner}
                          ></i>
                        </div>
                      </div>
                      <div style={styles.cardContent}>
                        <div style={styles.cardName}>
                          {formatName(item.name)}
                        </div>
                        <div style={styles.cardSubtitle}>{item.position}</div>
                      </div>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        ) : (
          <div
            style={{
              minHeight: "260px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "24px",
              color: "#6b7280",
            }}
          >
            <i
              className="fa-solid fa-check-circle"
              style={{
                fontSize: "24px",
                color: "#22c55e",
                marginBottom: "8px",
              }}
            ></i>
            <p style={{ fontSize: "12px", fontWeight: 500, margin: 0 }}>
              No hay bajas en el período seleccionado
            </p>
          </div>
        )}
      </div>

      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          style: {
            backgroundColor: "#ffffff",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            borderRadius: "8px",
          },
        }}
      >
        <MenuItem
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            fontSize: "16px",
          }}
          onClick={() => {
            if (contextMenu) {
              const url = `${window.location.origin}/colaboradores/detalles/${contextMenu.partnerId}`;
              window.open(url, "_blank", "noopener,noreferrer");
            }
            setContextMenu(null);
          }}
        >
          <i
            className="fa-regular fa-user"
            style={{ marginRight: "12px", fontSize: "18px" }}
          ></i>
          Perfil
        </MenuItem>
      </Menu>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  chartCard: {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
    width: "100%",
  },
  chartHeader: {
    background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
  },
  chartHeaderContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap",
  },
  chartTitle: {
    margin: 0,
    fontSize: "14px",
    fontWeight: "600",
    color: "#2d3748",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
  },
  chartTitleIcon: {
    fontSize: "16px",
    color: "#667eea",
  },
  dateLabel: {
    fontSize: "11px",
    color: "#6b7280",
    fontWeight: 500,
    marginRight: "8px",
    whiteSpace: "nowrap",
  },
  exportChartButton: {
    background: "#22c55e",
    border: "1px solid #22c55e",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    transition: "all 0.2s ease",
    minWidth: "auto",
  },
  exportChartIcon: {
    fontSize: "11px",
    color: "#fff",
  },
  exportChartText: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#fff",
  },
  chartBody: {
    padding: "0",
  },
  chartBodyWithChart: {},
  listWrapperWithChart: {},
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  card: {
    position: "relative",
    background: "#fff",
    borderRadius: "8px",
    padding: "6px 10px",
    transition: "all 0.2s ease",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  avatarContainer: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  avatarIcon: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f3f4f6",
  },
  avatarIconInner: {
    color: "#9ca3af",
    fontSize: "18px",
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
  },
  cardName: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#1f2937",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBottom: "2px",
  },
  cardSubtitle: {
    fontSize: "13px",
    color: "#6b7280",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  tooltipRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "6px",
  },
  tooltipIcon: {
    fontSize: "11px",
    color: "#9ca3af",
    width: "14px",
    flexShrink: 0,
    marginTop: "2px",
  },
  tooltipLabel: {
    color: "#d1d5db",
    fontWeight: 500,
    marginRight: "6px",
    flexShrink: 0,
  },
  tooltipValue: {
    color: "#fff",
    fontWeight: 400,
    wordBreak: "break-word",
  },
  inlineChartCard: {},
  chartSectionTitle: {},
  chartTooltip: {},
  chartTooltipLabel: {},
  chartTooltipValue: {},
};

export default UltimasBajas;
